import React, { useState } from "react";
import { showAlert } from "@/shared/lib/alert";
import { useFranchiseInquiry } from "../hooks/use-franchise-inquiry";

interface FranchiseInquiryFormProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

/**
 * FranchiseInquiryForm - 프랜차이즈 문의 폼 컴포넌트
 */
export const FranchiseInquiryForm = ({
  onClose,
  onSuccess,
}: FranchiseInquiryFormProps) => {
  const { submit, isLoading: isSubmitting } = useFranchiseInquiry();
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    has_existing_store: "",
    phone_number: "",
    description: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked);
    setAgreeTerms(checked);
    setAgreeMarketing(checked);
  };

  const handleTermsChange = (checked: boolean) => {
    setAgreeTerms(checked);
    if (!checked) {
      setAgreeAll(false);
    }
  };

  const handleMarketingChange = (checked: boolean) => {
    setAgreeMarketing(checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 필수 필드 검증
    if (!formData.name.trim()) {
      showAlert("이름을 입력해주세요.");
      return;
    }

    if (!formData.has_existing_store) {
      showAlert("점포 보유 여부를 선택해주세요.");
      return;
    }

    if (!formData.phone_number.trim()) {
      showAlert("휴대전화 번호를 입력해주세요.");
      return;
    }

    if (!formData.description.trim()) {
      showAlert("상세내용을 입력해주세요.");
      return;
    }

    if (!agreeTerms) {
      showAlert("개인 정보 수집 및 활용에 동의해주세요.");
      return;
    }

    try {
      await submit({
        name: formData.name,
        phone_number: formData.phone_number,
        description: formData.description,
        has_existing_store: formData.has_existing_store === "유",
        agree_terms: agreeTerms,
        agree_marketing: agreeMarketing,
      });

      showAlert("문의가 정상적으로 접수되었습니다.");
      setFormData({
        name: "",
        has_existing_store: "",
        phone_number: "",
        description: "",
      });
      setAgreeAll(false);
      setAgreeTerms(false);
      setAgreeMarketing(false);

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Error submitting franchise inquiry:", error);
      showAlert("문의 제출 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="w-full min-h-screen bg-white pt-20 pb-20">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-20 text-center">
          <h2 className="text-4xl font-extrabold text-black mb-4">
            프랜차이즈 문의
          </h2>
          <p className="text-lg text-black leading-7">
            전문가가 설계한 맞춤형 콘텐츠를 무료로 체험하고
            <br />
            우리 아이에게 꼭 맞는 성장 솔루션을 만나보세요.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl">
          {/* Name Field */}
          <div className="flex flex-col gap-3">
            <label className="text-lg font-medium text-black">
              이름<span className="text-orange-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="이름을 입력해 주세요."
              className="w-full h-12 px-4 py-3 rounded-[10px] border border-zinc-300 text-base placeholder:text-black/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
              maxLength={100}
            />
          </div>

          {/* Store Ownership Field */}
          <div className="flex flex-col gap-3">
            <label className="text-lg font-medium text-black">
              점포 보유<span className="text-orange-500">*</span>
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, has_existing_store: "유" })
                }
                className={`flex-1 h-12 rounded-[10px] border text-base font-medium transition-all ${
                  formData.has_existing_store === "유"
                    ? "border-orange-500 bg-orange-50 text-orange-500"
                    : "border-zinc-300 text-black/50 hover:border-orange-500"
                }`}
              >
                유
              </button>
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, has_existing_store: "무" })
                }
                className={`flex-1 h-12 rounded-[10px] border text-base font-medium transition-all ${
                  formData.has_existing_store === "무"
                    ? "border-orange-500 bg-orange-50 text-orange-500"
                    : "border-zinc-300 text-black/50 hover:border-orange-500"
                }`}
              >
                무
              </button>
            </div>
          </div>

          {/* Phone Number Field */}
          <div className="flex flex-col gap-3">
            <label className="text-lg font-medium text-black">
              휴대전화 번호<span className="text-orange-500">*</span>
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="-를 제외한 휴대전화번호를 입력해 주세요."
              className="w-full h-12 px-4 py-3 rounded-[10px] border border-zinc-300 text-base placeholder:text-black/50 focus:outline-none focus:ring-2 focus:ring-orange-500"
              maxLength={20}
            />
          </div>

          {/* Description Field */}
          <div className="flex flex-col gap-3">
            <label className="text-lg font-medium text-black">
              상세내용<span className="text-orange-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="상세 내용을 입력해 주세요."
              rows={8}
              className="w-full px-4 py-3 rounded-[10px] border border-zinc-300 text-base placeholder:text-black/50 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
          </div>

          {/* Agree All Section */}
          <div className="flex gap-8 p-7 rounded-[10px] border border-zinc-300">
            <input
              type="checkbox"
              id="agree_all"
              checked={agreeAll}
              onChange={(e) => handleAgreeAll(e.target.checked)}
              className="w-7 h-7 mt-1 rounded-sm border border-zinc-300 cursor-pointer accent-orange-500 flex-shrink-0"
            />
            <div className="flex-1">
              <p className="text-lg font-medium text-black mb-3">
                모두 동의합니다.
              </p>
              <p className="text-sm text-neutral-400 leading-5">
                이용약관, 개인정보 수집 및 활용(필수)/마케팅 활용(선택)에 대해
                모두 동의 합니다.
                <br />
                전체동의는 필수 및 선택정보에 대한 동의도 포함되어 있으며,
                개별적으로도 동의를 선택하실 수 있습니다.
                <br />
                선택 항목은 동의를 거부하시는 경우에도 서비스의 이용은
                가능하십니다.
              </p>
            </div>
          </div>

          {/* Individual Agreements Section */}
          <div className="p-7 rounded-[10px] border border-zinc-300 space-y-4">
            {/* Terms Agreement */}
            <div className="flex gap-8 items-start">
              <input
                type="checkbox"
                id="agree_terms"
                checked={agreeTerms}
                onChange={(e) => handleTermsChange(e.target.checked)}
                className="w-7 h-7 mt-0.5 rounded-sm border border-zinc-300 cursor-pointer accent-orange-500 flex-shrink-0"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="agree_terms"
                    className="text-lg font-normal text-black cursor-pointer"
                  >
                    개인 정보 수집 및 활용 동의
                    <span className="text-orange-500">(필수)</span>
                  </label>
                  <button
                    type="button"
                    className="h-7 px-2 py-0.5 rounded-[100px] border border-zinc-300 text-xs text-black/50 hover:text-black transition-colors"
                  >
                    전체보기
                  </button>
                </div>
              </div>
            </div>

            {/* Marketing Agreement */}
            <div className="flex gap-8 items-start">
              <input
                type="checkbox"
                id="agree_marketing"
                checked={agreeMarketing}
                onChange={(e) => handleMarketingChange(e.target.checked)}
                className="w-7 h-7 mt-0.5 rounded-sm border border-zinc-300 cursor-pointer accent-orange-500 flex-shrink-0"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="agree_marketing"
                    className="text-lg font-normal text-black cursor-pointer"
                  >
                    마케팅 활용 동의
                    <span className="text-black/50">(선택)</span>
                  </label>
                  <button
                    type="button"
                    className="h-7 px-2 py-0.5 rounded-[100px] border border-zinc-300 text-xs text-black/50 hover:text-black transition-colors"
                  >
                    전체보기
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-36 h-14 bg-orange-500 text-white rounded-[100px] text-lg font-medium hover:bg-orange-600 disabled:bg-gray-400 transition-colors"
            >
              {isSubmitting ? "문의 중..." : "문의하기"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
